<script setup lang="ts">
    import { computed, inject, ref } from 'vue'
    import { storeToRefs } from 'pinia'
    import { useI18n } from 'vue-i18n'
    import type { Chat as Conversation, UIChatMessage } from 'models'
    import { stripMarkdown, getTextPart } from 'utils'
    import {
        useChatbotStore,
        useConversationActions,
        useDialogConfirm,
    } from 'composables'
    import PkRelativeTime from '../PkRelativeTime.vue'
    import { ChatbotProfileContextKey } from './chatbotProfileContext'

    const props = defineProps<{ agentId: string }>()

    const { t: $t } = useI18n({ useScope: 'global' })

    const store = useChatbotStore(props.agentId)
    const { conversations, isLoadingConversations, localChatId } =
        storeToRefs(store)
    const { navigate, startNewChat, renameChatTitle, deleteChat } = store

    const {
        editingId,
        editingTitle,
        startEdit: startEditConversation,
        confirmEdit,
        cancelEdit,
        onRenameKeydown,
        requestDelete: handleDelete,
    } = useConversationActions({ renameChatTitle, deleteChat })

    const openConversation = (id: string) => {
        if (editingId.value) {
            return
        }
        localChatId.value = id
        navigate('chat')
    }

    const isCurrentConversation = (conversation: Conversation) => {
        return localChatId.value === conversation.id
    }

    const getLastMessage = (
        messages: UIChatMessage[],
        role: 'user' | 'assistant',
    ) => {
        return stripMarkdown(
            getTextPart(messages.filter((m) => m.role === role).slice(-1)[0]) ||
                '',
        )
    }

    const startEdit = (conversation: Conversation) => {
        startEditConversation(
            conversation,
            getLastMessage(conversation.messages, 'assistant'),
        )
    }

    const { openDialogConfirm } = useDialogConfirm()

    // GDPR B.3 — bulk "delete my conversations", available to anonymous
    // sessions too (the host wires the endpoint through the profile context).
    // Hidden when the host application owns the session (external token or
    // host-session mode): the endpoint erases ALL conversations of the
    // session's user platform-wide, an account-scoped action that belongs to
    // the host, not to an embedded widget. Same gating as sign-out and
    // manage-profile in PkChatbotViewProfile.
    const profile = inject(ChatbotProfileContextKey, undefined)
    const canEraseConversations = computed(
        () =>
            !!profile?.eraseConversations &&
            !(profile.isExternalAuth?.value ?? false),
    )
    const isErasingConversations = ref(false)
    const handleEraseAll = async () => {
        const eraseConversations = profile?.eraseConversations
        if (!eraseConversations || !canEraseConversations.value) {
            return
        }
        const proceed = await openDialogConfirm({
            title: $t('action.deleteMyConversations'),
            questionLabel: $t('message.deleteMyConversationsConfirm'),
        })
        if (!proceed) {
            return
        }
        isErasingConversations.value = true
        try {
            await eraseConversations()
            conversations.value = []
            startNewChat()
        } catch {
            // Same policy as single delete: fail silently and reconcile
            // with the server state
            store.loadConversations(props.agentId)
        } finally {
            isErasingConversations.value = false
        }
    }
</script>

<template>
    <div class="flex flex-col flex-1 min-h-0 relative">
        <!-- Loading skeleton -->
        <div
            v-if="isLoadingConversations && conversations.length === 0"
            class="flex flex-col gap-8 p-16 vv-skeleton">
            <div
                v-for="i in 5"
                :key="i"
                class="vv-skeleton__item rounded-md h-64" />
        </div>

        <!-- Empty state -->
        <div
            v-else-if="conversations.length === 0"
            class="flex flex-col flex-1 items-center justify-center gap-12 p-24 text-center">
            <div
                class="w-64 h-64 rounded-full bg-surface-1 border border-surface-3 flex items-center justify-center">
                <VvIcon name="ri:chat-3-line" class="w-28 h-28 text-word-4" />
            </div>
            <div class="flex flex-col gap-4">
                <strong class="text-16 font-semibold text-word-1">
                    {{ $t('message.noConversations') }}
                </strong>
                <span class="text-14 text-word-3 leading-relaxed">
                    {{ $t('message.noConversationsHint') }}
                </span>
            </div>
            <VvButton
                modifiers="primary rounded"
                icon="ri:add-line"
                class="mt-8"
                :label="$t('action.startNewChat')"
                @click="startNewChat()" />
        </div>

        <!-- Conversation list -->
        <ul
            v-else
            class="flex flex-col gap-8 overflow-y-auto p-16 pb-64 light-scrollbar">
            <li v-for="conversation in conversations" :key="conversation.id">
                <button
                    type="button"
                    class="w-full block rounded-md border border-surface-3 p-12 text-left text-14 leading-relaxed cursor-pointer transition-colors hover:border-surface-5 hover:bg-surface-1 focus-within:border-word"
                    :class="{
                        'border-brand bg-surface-1':
                            isCurrentConversation(conversation),
                    }"
                    @click="openConversation(conversation.id)">
                    <div class="flex gap-8">
                        <template v-if="editingId === conversation.id">
                            <VvInputText
                                v-model="editingTitle"
                                class="flex-1"
                                modifiers="compact"
                                autofocus
                                select-on-focus
                                :maxlength="255"
                                :placeholder="$t('placeholder.insert')"
                                @click.stop
                                @keydown="
                                    onRenameKeydown($event, conversation.id)
                                " />
                            <VvButtonGroup modifiers="compact">
                                <VvButton
                                    icon="ri:save-line"
                                    :title="$t('action.save')"
                                    modifiers="action-quiet-small"
                                    @click.stop="
                                        confirmEdit(conversation.id)
                                    " />
                                <VvButton
                                    icon="ri:close-line"
                                    :title="$t('action.cancel')"
                                    modifiers="action-quiet-small"
                                    @click.stop="cancelEdit()" />
                            </VvButtonGroup>
                        </template>
                        <template v-else>
                            <strong class="font-bold truncate block flex-1">
                                {{
                                    stripMarkdown(conversation.title) ||
                                    getLastMessage(
                                        conversation.messages,
                                        'assistant',
                                    )
                                }}
                            </strong>
                            <PkRelativeTime
                                class="text-word-4 text-smaller shrink-0"
                                :date="conversation.lastMessageAt" />
                            <div @click.stop>
                                <VvDropdown
                                    v-bind="{
                                        placement: 'bottom-end',
                                        modifiers: 'menu',
                                        flip: true,
                                        offset: 3,
                                        strategy: 'fixed',
                                    }">
                                    <VvButton
                                        icon="ri:more-2-fill"
                                        :title="$t('action.moreActions')"
                                        modifiers="action-quiet-small" />
                                    <template #items>
                                        <VvDropdownAction
                                            @click="startEdit(conversation)">
                                            <VvIcon name="ri:pencil-line" />
                                            {{ $t('action.renameChat') }}
                                        </VvDropdownAction>
                                        <VvDropdownAction
                                            @click="
                                                handleDelete(conversation.id)
                                            ">
                                            <VvIcon name="ri:delete-bin-line" />
                                            {{ $t('action.delete') }}
                                        </VvDropdownAction>
                                    </template>
                                </VvDropdown>
                            </div>
                        </template>
                    </div>
                    <span
                        v-if="editingId !== conversation.id"
                        class="text-12 text-word-4 line-clamp-2">
                        {{
                            stripMarkdown(conversation.summary) ||
                            getLastMessage(conversation.messages, 'user')
                        }}
                    </span>
                </button>
            </li>
        </ul>

        <!-- Sticky "new chat" CTA (hidden on empty state, which has its own CTA) -->
        <div
            v-if="conversations.length > 0"
            class="absolute bottom-0 left-0 right-0 flex flex-col items-center gap-4 px-16 pb-16 pt-32 pointer-events-none"
            :style="{
                background:
                    'linear-gradient(to top, var(--color-surface) 55%, transparent)',
            }">
            <VvButton
                class="text-14 pointer-events-auto"
                modifiers="primary rounded"
                icon="ri:add-line"
                :label="$t('action.startNewChat')"
                @click.stop="startNewChat()" />
            <VvButton
                v-if="canEraseConversations"
                class="text-12 pointer-events-auto"
                modifiers="action-quiet-danger"
                icon="ri:delete-bin-line"
                :loading="isErasingConversations"
                :disabled="isErasingConversations"
                :label="$t('action.deleteMyConversations')"
                @click.stop="handleEraseAll()" />
        </div>
    </div>
</template>
